import React from 'react';
import { Typography, Box, Stack, Divider } from '@mui/material';
import { Link } from 'react-router-dom';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import { trackArtistContentClick, trackReviewContentClick } from '../../lib/gaEvents';
import { CONTENT_TYPE, ARTIST_ACTION_TYPE, REVIEW_SECTION } from '../../constants/gaEnums';
import { useUserStore } from '../../stores/userStore';
import ReviewCount from '../common/ReviewCount';
import BookmarkCount from '../common/BookmarkCount';
import RatingReadOnly from '../common/RatingReadOnly';
import AlbumCover from '../common/AlbumCover';
import theme from '../../theme';
import deleteIcon from '../../assets/delete.svg';
import refreshIcon from '../../assets/refresh.svg';
import ArtistLink from '../common/ArtistLink';
import AlbumTitleLink from '../common/AlbumTitleLink';
import addIcon from '../../assets/add.svg';

/**
 * AlbumCard - 앨범 카드 컴포넌트
 *
 * 1. default
 * - /reviews ✅
 * - /search/reviews ✅
 * - /reviews/{source}/{id}/related-albums ✅
 * [lg]               [md,xs]
 * +-------------+    +-----------+
 * |    image    |    |   image   |
 * +-------------+    +-----------+
 * | title       |    | title     |
 * | artist      |    | artist    |
 * | rating|RC BC|    | rating    |
 * | date        |    | date|RC BC|
 * +-------------+    +-----------+
 *
 * 1-1. non artist
 * - /artist/{source}/{id}/full-albums ✅
 * - /artist/{source}/{id}/singles ✅
 * [lg]               [md, xs]
 * +-------------+    +-----------+
 * |    image    |    |   image   |
 * +-------------+    +-----------+
 * | title       |    | title     |
 * | rating|RC BC|    | rating    |
 * | date        |    | date|RC BC|
 * +-------------+    +-----------+
 *
 * 1-2. non artist - onNewLine
 * - /artist/{source}/{id}의 앨범, 싱글 및 EP ✅
 * [lg]               [md]             [xs]
 * +-------------+    +-----------+    +-----------+
 * |    image    |    |   image   |    |   image   |
 * +-------------+    +-----------+    +-----------+
 * | title       |    | title     |    | title     |
 * | rating|RC BC|    | rating    |    | rating    |
 * | date        |    | date|RC BC|    | date      |
 * +-------------+    +-----------+    | RC BC     |
 *                                     +-----------+
 *
 * 1-3. summary - 작은 화면에서는 date를 생략하는 버전
 * - /search/summary ✅
 * [lg]               [md]             [xs]
 * +-------------+    +-----------+    +-----------+
 * |    image    |    |   image   |    |   image   |
 * +-------------+    +-----------+    +-----------+
 * | title       |    | title     |    | title     |
 * | artist      |    | artist    |    | artist    |
 * | rating|RC BC|    | rating    |    | rating    |
 * | date        |    | date|RC BC|    | RC BC     |
 * +-------------+    +-----------+    +-----------+
 *
 * 1-4. compact
 * 5. /reviews/{source}/{id}의 좋아할 만한 다른 앨범 ✅
 * 6. /mypage/saved/reviews (+ saved 버튼)
 * [lg, md]             [xs]
 * +-----------+    +-----------+
 * |   image   |    |   image   |
 * +-----------+    +-----------+
 * | title     |    | title     |
 * | artist    |    | artist    |
 * | rating    |    | rating    |
 * | date|RC BC|    | date      |
 * +-----------+    | RC BC     |
 *                  +-----------+
 */
const AlbumVerticalCard = ({
  albumId,
  sourceId,
  albumSource,
  slug,
  imageUrl,
  albumName,
  artistId,
  artistDbId,
  artistSource,
  artistName,
  artistSlug,
  albumAvgRating = 0,
  albumGenres = [],
  showGenres = false,
  hideBookmarkCount = false,
  commentCount,
  bookmarkCount,
  releaseDate = null,
  contentRating = 'CLEAN',
  isStarred = false,
  isEditMode = false,
  isMarkedForDeletion = false,
  onMarkForDeletion,
  onUndoDeletion,
  isAddMode = false,
  onAdd,
  hideArtist = false,
  ratingSolo = false,
  dateSolo = false,
  compact = false,
  smallText = false,
  hideReviewMetaOnMobile = false,
  actionButton,
  coverActionButton,
  referrerType,
  referrerSection,
  artistSection,
  pageType,
}) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const user = useUserStore((state) => state.user);

  const albumUrl = slug
    ? albumId
      ? `/reviews/${slug}`
      : `/reviews/${slug}?source_id=${sourceId}`
    : `/reviews/${albumSource.toLowerCase()}/${sourceId}`;
  const albumLinkState = {
    sourceId,
    sourceType: albumSource,
    ...(referrerType && { referrerType }),
    ...(referrerSection && { referrerSection }),
  };
  const visibleGenres = Array.isArray(albumGenres) ? albumGenres.filter(Boolean).slice(0, 3) : [];

  const handleImageClick = (e) => {
    // Cmd/Ctrl + 클릭이 아닌 경우에만 기본 네비게이션
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      if (artistSection) {
        trackArtistContentClick(
          {
            artistSection,
            actionType: ARTIST_ACTION_TYPE.ALBUM_CLICK,
            contentType: CONTENT_TYPE.ALBUM,
            contentId: sourceId,
            contentName: albumName,
            pageType,
          },
          user,
        );
      } else if (referrerSection === REVIEW_SECTION.RECOMMENDED_ALBUM) {
        trackReviewContentClick(
          {
            section: REVIEW_SECTION.RECOMMENDED_ALBUM,
            contentType: CONTENT_TYPE.ALBUM,
            contentId: sourceId,
            contentName: albumName,
          },
          user,
        );
      }
      navigateWithScrollMemory(albumUrl, { state: albumLinkState });
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: compact ? { xs: 1, md: 1.5, lg: 1 } : { xs: 1, md: 1.25, lg: 1.5 },
        position: 'relative',
      }}
    >
      {/* Section1: 이미지 */}
      <Box sx={{ width: '100%', position: 'relative' }}>
        {isEditMode && (
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 12, lg: 16 },
              right: { xs: 12, lg: 16 },
              zIndex: 10,
            }}
          >
            <Box
              component="img"
              src={isMarkedForDeletion ? refreshIcon : deleteIcon}
              alt={isMarkedForDeletion ? 'Undo deletion' : 'Mark for deletion'}
              onClick={(e) => {
                e.stopPropagation();
                if (isMarkedForDeletion) {
                  onUndoDeletion?.(sourceId);
                } else {
                  onMarkForDeletion?.(sourceId);
                }
              }}
              sx={{
                width: { xs: 24, lg: 32 },
                height: { xs: 24, lg: 32 },
                cursor: 'pointer',
              }}
            />
          </Box>
        )}
        {isAddMode && (
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 12, lg: 16 },
              right: { xs: 12, lg: 16 },
              zIndex: 10,
            }}
          >
            <Box
              component="img"
              src={addIcon}
              alt="Add"
              onClick={(e) => {
                e.stopPropagation();
                onAdd?.();
              }}
              sx={{
                width: { xs: 24, lg: 32 },
                height: { xs: 24, lg: 32 },
                cursor: 'pointer',
              }}
            />
          </Box>
        )}
        <Link
          to={albumUrl}
          state={albumLinkState}
          style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
        >
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <AlbumCover
              onClick={handleImageClick}
              imageUrl={imageUrl}
              altText={albumName}
              isStarred={isStarred}
              sx={{
                aspectRatio: '1 / 1',
                objectFit: 'cover',
                width: '100%',
              }}
            />
            {isMarkedForDeletion && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 1,
                }}
              />
            )}
            {coverActionButton && (
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                sx={{
                  position: 'absolute',
                  top: { xs: 8, md: 10 },
                  right: { xs: 8, md: 10 },
                  zIndex: 3,
                }}
              >
                {coverActionButton}
              </Box>
            )}
          </Box>
        </Link>
      </Box>

      {/* Section2: 앨범 정보 */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
        }}
      >
        <Stack sx={{ minWidth: 0, flex: 1 }}>
          <AlbumTitleLink
            title={albumName}
            to={albumUrl}
            state={albumLinkState}
            contentRating={contentRating}
            sx={{
              fontSize: compact
                ? { xs: 13, md: 14 }
                : smallText
                  ? { xs: 13, md: 14, lg: 16 }
                  : { xs: 14, lg: 16 },
              lineHeight: 1.25,
              minHeight: compact ? undefined : { xs: 17.5, lg: 20 },
              mb: compact ? 0 : 0.75,
            }}
            iconSx={{
              width: compact
                ? { xs: '10px', md: '11px', lg: '10px' }
                : { xs: '10px', md: '11px', lg: '12px' },
              height: compact
                ? { xs: '10px', md: '11px', lg: '10px' }
                : { xs: '10px', md: '11px', lg: '12px' },
              verticalAlign: '-0.02em',
            }}
          />
          {!hideArtist && (
            <ArtistLink
              artistName={artistName}
              artistSource={artistSource}
              artistSourceId={artistId}
              artistSlug={artistSlug}
              artistDbId={artistDbId}
              sx={{
                fontSize: compact ? 13 : { xs: 13, lg: 14 },
                color: theme.palette.black[4],
                WebkitLineClamp: 1,
                lineHeight: 1.25,
                minHeight: compact ? undefined : { xs: 16.25, lg: 17.5 },
                mb: compact ? 0 : 0.75,
              }}
            />
          )}

          {showGenres && (
            <Typography
              sx={{
                display: visibleGenres.length ? '-webkit-box' : 'block',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: compact ? 13 : { xs: 13, lg: 14 },
                lineHeight: 1.25,
                minHeight: compact ? undefined : { xs: 16.25, lg: 17.5 },
                color: theme.palette.black[4],
                mb: compact ? 0 : 0.75,
              }}
            >
              {visibleGenres.join(', ')}
            </Typography>
          )}

          <Typography
            fontSize={compact ? { xs: 12, md: 13 } : { xs: 12, md: 13, lg: 14 }}
            lineHeight={1.25}
            color={theme.palette.black[4]}
            sx={{
              minHeight: compact ? undefined : { xs: 15, md: 16.25, lg: 17.5 },
              mb: compact ? 0 : 0.75,
            }}
          >
            {releaseDate}
          </Typography>

          {/* Rating 줄 */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: compact ? 1.5 : 0 }}>
            <RatingReadOnly
              value={albumAvgRating}
              variant={compact ? 'fullResponsiveCompact' : 'fullResponsive'}
            />
            {(!ratingSolo || hideBookmarkCount) && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    height: '8px',
                    alignSelf: 'center',
                    backgroundColor: theme.palette.grey.line,
                    display: hideReviewMetaOnMobile ? { xs: 'none', md: 'block' } : 'block',
                  }}
                />
                <Stack
                  direction="row"
                  spacing={{ xs: 1, md: 1.5 }}
                  alignItems="center"
                  sx={{ display: hideReviewMetaOnMobile ? { xs: 'none', md: 'flex' } : 'flex' }}
                >
                  <ReviewCount reviewCount={commentCount} />
                  {!hideBookmarkCount && <BookmarkCount bookmarkCount={bookmarkCount} />}
                </Stack>
              </>
            )}
          </Stack>

          {/* RC BC 단독 줄 */}
          {ratingSolo && !hideBookmarkCount && (
            <Stack
              direction="row"
              spacing={{ xs: 1, md: 1.5 }}
              alignItems="center"
              sx={{ mt: compact ? 1.5 : 0.75 }}
            >
              <ReviewCount reviewCount={commentCount} />
              <BookmarkCount bookmarkCount={bookmarkCount} />
            </Stack>
          )}
        </Stack>

        {actionButton}
      </Box>
    </Box>
  );
};

export default AlbumVerticalCard;
